import React from "react";
import "./ui.css";

/**
 * Field – popisek + jeden ovládací prvek (input / select / textarea nebo
 * helper Field.Number, Field.Date, Field.DateTime, Field.Textarea) + nápověda,
 * info a chyba. Field dítě naklonuje a doplní mu id, aria-describedby,
 * aria-invalid, required a třídu is-invalid; žádný vlastní stav ani efekty.
 *
 * Id ovládacího prvku zůstává přesně `id` (handleInputChange čte e.target.id);
 * odvozená id: `${id}-help`, `${id}-info`, `${id}-error`, `${id}-label`.
 */

export type FieldLayout = "stacked" | "row";

/** Props, které Field doplní klonovanému ovládacímu prvku. */
export interface FieldControlProps {
  id?: string;
  className?: string;
  required?: boolean;
  "aria-describedby"?: string;
  "aria-invalid"?: React.AriaAttributes["aria-invalid"];
}

interface FieldBaseProps {
  /** id ovládacího prvku (a základ odvozených id). */
  id: string;
  label: string;
  /** Hvězdička u popisku + atribut required na ovládacím prvku. */
  required?: boolean;
  /** Nápověda pod polem (form-text). */
  help?: string;
  /** Chybová hláška: červený text s role="alert", pole dostane is-invalid + aria-invalid. */
  error?: string;
  /** Vysvětlivka s ikonou pod polem – náhrada title tooltipu, který na dotyku nefunguje. */
  info?: string;
  /** stacked = popisek nad polem; row = od 576 px popisek vlevo (col-sm-4 / col-sm-8), pod tím stacked. Výchozí stacked. */
  layout?: FieldLayout;
  className?: string;
}

export interface FieldControlFieldProps extends FieldBaseProps {
  /** Výchozí režim: dítě je jeden ovládací prvek, který Field naklonuje. */
  group?: false;
  children: React.ReactElement<FieldControlProps>;
}

export interface FieldGroupProps extends FieldBaseProps {
  /**
   * Skupina: děti nejsou jeden ovládací prvek (vlastní komponenta, seznam
   * otázek…). Field je neklonuje, vykreslí role="group" s aria-labelledby.
   */
  group: true;
  children: React.ReactNode;
}

export type FieldProps = FieldControlFieldProps | FieldGroupProps;

/** Spojí třídy a vynechá prázdné hodnoty. */
const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes.filter(Boolean).join(" ");

/** Spojí id pro aria-describedby; prázdný výsledek = undefined (atribut se nevykreslí). */
const joinIds = (...ids: Array<string | false | null | undefined>): string | undefined =>
  ids.filter(Boolean).join(" ") || undefined;

const FieldBase: React.FC<FieldProps> = (props) => {
  const {
    id,
    label,
    required,
    help,
    error,
    info,
    layout = "stacked",
    className,
  } = props;

  const helpId = `${id}-help`;
  const infoId = `${id}-info`;
  const errorId = `${id}-error`;
  const labelId = `${id}-label`;
  const describedBy = joinIds(help && helpId, info && infoId, error && errorId);
  const isRow = layout === "row";

  const labelContent = (
    <>
      {label}
      {required && (
        <span className="lks-field__req" aria-hidden="true">
          *
        </span>
      )}
    </>
  );

  const messages = (
    <>
      {help && (
        <div id={helpId} className="form-text lks-field__help">
          {help}
        </div>
      )}
      {info && (
        <div id={infoId} className="form-text lks-field__info">
          <i className="bi bi-info-circle" aria-hidden="true" />
          <span>{info}</span>
        </div>
      )}
      {error && (
        <div
          id={errorId}
          className="invalid-feedback d-block lks-field__error"
          role="alert"
        >
          {error}
        </div>
      )}
    </>
  );

  let control: React.ReactNode;
  if (props.group === true) {
    control = props.children;
  } else {
    const child = props.children;
    const injected: Partial<FieldControlProps> = {
      id,
      className: cx(child.props.className, error && "is-invalid") || undefined,
      "aria-describedby": joinIds(child.props["aria-describedby"], describedBy),
      "aria-invalid": error ? true : child.props["aria-invalid"],
    };
    if (required !== undefined) injected.required = required;
    control = React.cloneElement(child, injected);
  }

  const wrapperClass = cx(
    "lks-field",
    isRow ? "row lks-field--row" : "lks-field--stacked",
    className
  );

  const labelClass = cx(
    "lks-field__label",
    isRow ? "col-sm-4 col-form-label" : "form-label"
  );

  if (props.group === true) {
    return (
      <div
        className={wrapperClass}
        role="group"
        aria-labelledby={labelId}
        aria-describedby={describedBy}
      >
        <div id={labelId} className={labelClass}>
          {labelContent}
        </div>
        {isRow ? (
          <div className="col-sm-8">
            {control}
            {messages}
          </div>
        ) : (
          <>
            {control}
            {messages}
          </>
        )}
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <label htmlFor={id} className={labelClass}>
        {labelContent}
      </label>
      {isRow ? (
        <div className="col-sm-8">
          {control}
          {messages}
        </div>
      ) : (
        <>
          {control}
          {messages}
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
//  Helpery ovládacích prvků (vždy form-control bez -sm)
// ─────────────────────────────────────────────────────────────

type NativeInputProps = React.InputHTMLAttributes<HTMLInputElement>;

export interface NumberInputProps
  extends Omit<
    NativeInputProps,
    "type" | "value" | "onChange" | "prefix" | "inputMode"
  > {
  /** Prázdné pole = "" / undefined / null. */
  value: number | "" | null | undefined;
  /** Nativní událost – kompatibilní s handleInputChange(e), které čte e.target.id. */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /** Hodnota převedená na číslo; prázdné nebo neplatné pole = undefined. */
  onValueChange?: (value: number | undefined) => void;
  /** Text před polem (input-group-text). */
  prefix?: string;
  /** Text za polem, např. "Kč" nebo "střelců". */
  suffix?: string;
  /** numeric = celá čísla (výchozí), decimal = desetinná klávesnice. */
  inputMode?: "numeric" | "decimal";
  /** Omezí šířku pole (např. 120 pro kapacitu); na mobilu jinak zabírá celou šířku. */
  maxWidth?: number | string;
}

export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      value,
      onChange,
      onValueChange,
      prefix,
      suffix,
      inputMode = "numeric",
      maxWidth,
      className,
      ...rest
    },
    ref
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) onChange(e);
      if (onValueChange) {
        const raw = e.target.value;
        const parsed = raw === "" ? NaN : Number(raw);
        onValueChange(Number.isNaN(parsed) ? undefined : parsed);
      }
    };

    const input = (
      <input
        ref={ref}
        type="number"
        inputMode={inputMode}
        className={cx("form-control", className)}
        value={value === null || value === undefined ? "" : value}
        onChange={handleChange}
        {...rest}
      />
    );

    const style = maxWidth !== undefined ? { maxWidth } : undefined;

    if (!prefix && !suffix) {
      return style ? <div style={style}>{input}</div> : input;
    }

    return (
      <div className="input-group" style={style}>
        {prefix && <span className="input-group-text">{prefix}</span>}
        {input}
        {suffix && <span className="input-group-text">{suffix}</span>}
      </div>
    );
  }
);
NumberInput.displayName = "Field.Number";

export interface DateInputProps
  extends Omit<NativeInputProps, "type" | "value" | "onChange"> {
  /** "YYYY-MM-DD" (Field.Date) / "YYYY-MM-DDTHH:mm" (Field.DateTime) nebo "". */
  value: string;
  /** Nativní událost – kompatibilní s handleInputChange(e). */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /** Hodnota jako řetězec (po vymazání ""). */
  onValueChange?: (value: string) => void;
  /** Zobrazí tlačítko × pro vymazání, je-li pole vyplněné. */
  clearable?: boolean;
  /** Volá se po kliknutí na ×; bez něj se zavolá onValueChange(""). */
  onClear?: () => void;
  /** Popisek tlačítka × pro čtečky a title. Výchozí "Vymazat datum". */
  clearLabel?: string;
  maxWidth?: number | string;
}

const DateLikeInput = React.forwardRef<
  HTMLInputElement,
  DateInputProps & { type: "date" | "datetime-local" }
>(
  (
    {
      type,
      value,
      onChange,
      onValueChange,
      clearable,
      onClear,
      clearLabel = "Vymazat datum",
      maxWidth,
      className,
      disabled,
      ...rest
    },
    ref
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) onChange(e);
      if (onValueChange) onValueChange(e.target.value);
    };

    const handleClear = () => {
      if (onClear) onClear();
      else if (onValueChange) onValueChange("");
    };

    const input = (
      <input
        ref={ref}
        type={type}
        className={cx("form-control", className)}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        {...rest}
      />
    );

    const style = maxWidth !== undefined ? { maxWidth } : undefined;
    const showClear = Boolean(clearable) && value !== "" && !disabled;

    if (!showClear) {
      return style ? <div style={style}>{input}</div> : input;
    }

    return (
      <div className="input-group" style={style}>
        {input}
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handleClear}
          aria-label={clearLabel}
          title={clearLabel}
        >
          <i className="bi bi-x-lg" aria-hidden="true" />
        </button>
      </div>
    );
  }
);
DateLikeInput.displayName = "Field.DateLike";

export const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  (props, ref) => <DateLikeInput ref={ref} type="date" {...props} />
);
DateInput.displayName = "Field.Date";

export const DateTimeInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  (props, ref) => <DateLikeInput ref={ref} type="datetime-local" {...props} />
);
DateTimeInput.displayName = "Field.DateTime";

export interface TextareaInputProps
  extends Omit<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    "value" | "onChange"
  > {
  value: string;
  /** Nativní událost – kompatibilní s handleInputChange(e). */
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  onValueChange?: (value: string) => void;
  /** Počítadlo "x/maxLength" pod polem; zobrazí se, je-li zadáno maxLength. Výchozí true. */
  showCounter?: boolean;
}

export const TextareaInput = React.forwardRef<
  HTMLTextAreaElement,
  TextareaInputProps
>(
  (
    {
      value,
      onChange,
      onValueChange,
      maxLength,
      showCounter = true,
      rows = 3,
      className,
      ...rest
    },
    ref
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (onChange) onChange(e);
      if (onValueChange) onValueChange(e.target.value);
    };

    return (
      <>
        <textarea
          ref={ref}
          className={cx("form-control", className)}
          value={value}
          onChange={handleChange}
          maxLength={maxLength}
          rows={rows}
          {...rest}
        />
        {maxLength !== undefined && showCounter && (
          <div
            className={cx(
              "lks-field__counter",
              value.length >= maxLength && "lks-field__counter--full"
            )}
          >
            {value.length}/{maxLength}
          </div>
        )}
      </>
    );
  }
);
TextareaInput.displayName = "Field.Textarea";

/**
 * Field s helpery jako statickými vlastnostmi:
 * <Field id="x" label="…"><Field.Number value={…} onChange={…} /></Field>
 */
export const Field = Object.assign(FieldBase, {
  Number: NumberInput,
  Date: DateInput,
  DateTime: DateTimeInput,
  Textarea: TextareaInput,
});

export default Field;
